import {
  isLiteralElement,
  isPoundElement,
} from "@formatjs/icu-messageformat-parser";
import { IntlErrorCode, type OnErrorFn } from "@formatjs/intl";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
} from "react";
import { IntlProvider } from "react-intl";

import useStorageState from "../useStorageState";
import en_messages from "./en.json";
import hu_messages from "./hu.json";
import ro_messages from "./ro.json";
import sk_messages from "./sk.json";

const defaultLocale = "hu";

const localizedMessages = {
  hu: hu_messages,
  en: en_messages,
  sk: sk_messages,
  ro: ro_messages,
};

export type LocaleName = keyof typeof localizedMessages;
const locales = Object.keys(localizedMessages) as LocaleName[];

function mapLocale(locale: unknown): LocaleName {
  if (typeof locale !== "string") {
    return defaultLocale;
  }

  if (locale in localizedMessages) {
    return locale as LocaleName;
  }

  const splitLocale = locale.split("-")[0].toLocaleLowerCase();
  if (splitLocale in localizedMessages) {
    return splitLocale as LocaleName;
  }

  return defaultLocale;
}

export interface Locale {
  locale: LocaleName;
  setLocale: (newLocale: string) => void;
  locales: LocaleName[];
}

const LocaleContext = createContext<Locale | undefined>(undefined);

export const useLocale = () => {
  const value = useContext(LocaleContext);
  if (value === undefined) {
    throw new Error("useLocale must be used inside LocaleProvider");
  }
  return value;
};

function onLocaleError(err: Parameters<OnErrorFn>[0]) {
  if (err.code !== IntlErrorCode.MISSING_TRANSLATION || !err.descriptor) {
    console.error("react-inlt locale error:", err);
    return;
  }

  if (import.meta.env.PROD) {
    return;
  }

  const {
    descriptor: { id, defaultMessage },
  } = err;

  console.warn(
    "Missing translation: ",
    `"${id}": "${
      typeof defaultMessage === "string"
        ? defaultMessage
        : (defaultMessage
            ?.map((m) =>
              isPoundElement(m)
                ? "#"
                : isLiteralElement(m)
                  ? m.value
                  : `{${m.value}}`,
            )
            .join("") ?? "")
    }"`,
  );
}

export default function LocaleProvider({ children }: PropsWithChildren) {
  const [locale, storeLocale] = useStorageState(
    "lang",
    mapLocale(navigator.language),
    false,
    mapLocale,
  );

  const setLocale = useCallback(
    (newLocale: string) => storeLocale(mapLocale(newLocale)),
    [storeLocale],
  );

  useEffect(() => {
    if (import.meta.env.PROD) {
      return;
    }

    if (!(locale in localizedMessages)) {
      console.warn(`Locale not found: ${locale}`);
      return;
    }

    const not_translated_prefix = `[${locale}]: `;

    for (const [id, str] of Object.entries(localizedMessages[locale])) {
      if (str.startsWith(not_translated_prefix)) {
        console.warn(`Missing translation: [${locale}] id=[${id}]: ${str}`);
      }
    }
  }, [locale]);

  return (
    <LocaleContext.Provider value={{ locale, locales, setLocale }}>
      <IntlProvider
        locale={locale}
        messages={localizedMessages[locale] ?? localizedMessages[defaultLocale]}
        defaultLocale={defaultLocale}
        onError={onLocaleError}
      >
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
}
